import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, can } from "@/context/AuthContext";
import { ProgressRing, StatusBadge, fmtDate, fmtDateTime, itemComplete, isCountable } from "@/components/common";
import { MaterialPreview } from "@/components/MaterialPreview";
import { ChecklistItem } from "@/components/ChecklistItem";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronDown, ArrowLeft, Save, Loader2, Download, Send, Mail,
  AlertCircle, CircleCheck, Plus, X, Award,
} from "lucide-react";

export default function ChecklistSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data: session, isLoading } = useQuery({
    queryKey: ["session", id],
    queryFn: async () => (await api.get(`/training-sessions/${id}`)).data,
  });

  const [progress, setProgress] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [submitOpen, setSubmitOpen] = useState(false);
  const [signoff, setSignoff] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [recipInput, setRecipInput] = useState("");
  const [previewId, setPreviewId] = useState(null);

  const { data: settings } = useQuery({
    queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data,
  });
  const pending = useRef({});
  const timer = useRef(null);

  const canEdit = can(user, "edit_training");
  const isCompleted = session?.status === "completed";
  const readonly = isCompleted || !canEdit;

  useEffect(() => {
    if (session) {
      setProgress(session.progress || {});
      setSavedAt(session.last_saved_at);
      const init = {};
      (session.template_snapshot?.sections || []).forEach((s, i) => { init[s.id] = i < 2; });
      setOpenSections(init);
      if (!signoff) setSignoff(user?.name || "");
    }
    // eslint-disable-next-line
  }, [session?.id]);

  const saveMutation = useMutation({
    mutationFn: async (updates) => (await api.patch(`/training-sessions/${id}/progress`, { progress: updates })).data,
    onSuccess: (data) => {
      setSavedAt(data.last_saved_at);
      setSaving(false);
      qc.invalidateQueries({ queryKey: ["sessions"] });
    },
    onError: () => { setSaving(false); toast.error("Autosave failed — retrying on next change"); },
  });

  const flush = () => {
    if (Object.keys(pending.current).length === 0) return;
    const updates = pending.current;
    pending.current = {};
    setSaving(true);
    saveMutation.mutate(updates);
  };

  const queueSave = (itemId, patch) => {
    pending.current[itemId] = { ...(pending.current[itemId] || {}), ...patch };
    setSaving(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 800);
  };

  const updateItem = (itemId, patch) => {
    if (readonly) return;
    setProgress((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), ...patch } }));
    queueSave(itemId, patch);
  };

  const manualSave = () => {
    if (timer.current) clearTimeout(timer.current);
    flush();
    toast.success("Progress saved");
  };

  const sections = session?.template_snapshot?.sections || [];
  const { total, done, requiredRemaining } = useMemo(() => {
    let total = 0, done = 0, req = [];
    sections.forEach((s) => s.items.forEach((it) => {
      if (!isCountable(it)) return;
      total++;
      if (itemComplete(it, progress[it.id])) done++;
      else if (it.required) req.push(it.label);
    }));
    return { total, done, requiredRemaining: req };
  }, [sections, progress]);

  const percent = total ? Math.round((done / total) * 100) : 0;

  const openSubmit = () => {
    setRecipients(settings?.recipient_emails || []);
    setSubmitOpen(true);
  };

  const addRecipient = () => {
    const e = recipInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email"); return; }
    if (!recipients.includes(e)) setRecipients([...recipients, e]);
    setRecipInput("");
  };

  const submitMutation = useMutation({
    mutationFn: async (finalRecipients) => (await api.post(`/training-sessions/${id}/submit`, { signoff_name: signoff, recipients: finalRecipients })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setSubmitOpen(false);
      const to = (data.recipients || []).join(", ");
      if (data.email_status === "sent") toast.success(to ? `Record emailed to ${to}` : "Training submitted");
      else if (data.email_status === "failed") toast.warning("Training submitted, but email failed. You can resend it later.");
      else toast.success("Training submitted (no recipients configured)");
      navigate("/dashboard");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Submit failed"),
  });

  const confirmSubmit = () => {
    let list = recipients;
    const pending = recipInput.trim().toLowerCase();
    if (pending) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(pending)) { toast.error("Enter a valid email or clear the box before submitting"); return; }
      if (!list.includes(pending)) list = [...list, pending];
      setRecipients(list);
      setRecipInput("");
    }
    if (list.length === 0) { toast.error("Add at least one recipient"); return; }
    submitMutation.mutate(list);
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/training-sessions/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = session.pdf_filename || `${session.session_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const resendMutation = useMutation({
    mutationFn: async () => (await api.post(`/training-sessions/${id}/resend-email`)).data,
    onSuccess: () => toast.success("Email resent to HR"),
    onError: (e) => toast.error(e?.response?.data?.detail || "Resend failed"),
  });

  if (isLoading || !session) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6 pb-24" data-testid="checklist-page">
      <Button variant="ghost" size="sm" className="gap-2 -ml-2" onClick={() => navigate("/dashboard")} data-testid="back-button">
        <ArrowLeft size={16} /> Back to Dashboard
      </Button>

      {/* Sticky header */}
      <Card className="sticky top-16 z-10 p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <ProgressRing value={isCompleted ? 100 : percent} size={68} />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-xl font-bold">{session.trainee_name}</h1>
                <StatusBadge status={session.status} />
              </div>
              <div className="text-sm text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                <span>{session.trainee_position}</span>
                <span className="opacity-40">•</span>
                <span>{session.training_type}</span>
                <span className="opacity-40">•</span>
                <span className="font-mono text-xs">{session.session_number}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Trainer: {session.trainer_name} · Started {fmtDate(session.training_start_date)}
                {session.location ? ` · ${session.location}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCompleted ? (
              <>
                <Button variant="secondary" className="gap-2" onClick={downloadPdf} data-testid="download-pdf-button">
                  <Download size={16} /> PDF
                </Button>
                {can(user, "resend_email") && (
                  <Button variant="outline" className="gap-2" disabled={resendMutation.isPending} onClick={() => resendMutation.mutate()} data-testid="resend-email-button">
                    {resendMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Resend
                  </Button>
                )}
              </>
            ) : canEdit ? (
              <>
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mr-1" data-testid="autosave-indicator">
                  {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
                    : <><CircleCheck size={13} className="text-success" /> Saved {savedAt ? fmtDateTime(savedAt) : ""}</>}
                </div>
                <Button variant="secondary" className="gap-2" onClick={manualSave} data-testid="save-progress-button">
                  <Save size={16} /> Save
                </Button>
                <Button className="gap-2" disabled={requiredRemaining.length > 0} onClick={openSubmit} data-testid="open-submit-button">
                  <Send size={16} /> Submit
                </Button>
              </>
            ) : null}
          </div>
        </div>
        {!readonly && requiredRemaining.length > 0 && (
          <div className="mt-3 text-xs text-warning flex items-center gap-1.5" data-testid="required-remaining">
            <AlertCircle size={13} /> {requiredRemaining.length} required item{requiredRemaining.length > 1 ? "s" : ""} remaining before you can submit
          </div>
        )}
      </Card>

      {/* Sections */}
      <div className="space-y-4">
        {sections.map((section) => {
          const countable = section.items.filter((it) => isCountable(it));
          const secDone = countable.filter((it) => itemComplete(it, progress[it.id])).length;
          return (
            <Card key={section.id} className="overflow-hidden" data-testid={`section-${section.id}`}>
              <Collapsible open={!!openSections[section.id]} onOpenChange={(o) => setOpenSections((p) => ({ ...p, [section.id]: o }))}>
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between p-5 hover:bg-muted/40 transition-colors" data-testid={`section-toggle-${section.id}`}>
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading font-semibold text-left">{section.title}</h3>
                      <Badge variant="outline" className="font-mono text-xs">{secDone}/{countable.length}</Badge>
                    </div>
                    <ChevronDown size={18} className={`transition-transform text-muted-foreground ${openSections[section.id] ? "rotate-180" : ""}`} />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="divide-y">
                    {section.items.map((item) => (
                      <ChecklistItem
                        key={item.id}
                        item={item}
                        p={progress[item.id] || {}}
                        readonly={readonly}
                        onChange={(patch) => updateItem(item.id, patch)}
                        onPreview={(mid) => setPreviewId(mid)}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {isCompleted && (
        <Card className="p-5" data-testid="signoff-block">
          <h3 className="font-heading font-semibold mb-2">Sign-off</h3>
          <p className="text-sm">Submitted by <span className="font-medium">{session.submitted_by}</span></p>
          <p className="text-xs text-muted-foreground font-mono mt-1">Completed {fmtDateTime(session.completed_at)} · {session.days_to_complete} day(s)</p>
          {session.recipients?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">Emailed to: {session.recipients.join(", ")}</p>
          )}
        </Card>
      )}

      {isCompleted && session.certificate && (
        <Card className="p-5 border-accent/40 bg-accent/5" data-testid="certificate-issued-card">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-success/15 text-success shrink-0">
              <Award size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading font-semibold">Certificate issued</h3>
              <p className="text-sm text-muted-foreground">
                {session.certificate.certificate_type_name} · <span className="font-mono">{session.certificate.certificate_number}</span>
              </p>
            </div>
            <Button variant="secondary" className="gap-2" onClick={() => window.open(`${API}/certificates/${session.certificate.id}/pdf`, "_blank")} data-testid="download-certificate-button">
              <Download size={16} /> Certificate
            </Button>
          </div>
        </Card>
      )}

      {/* Submit dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent data-testid="submit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Submit Training</DialogTitle>
            <DialogDescription>This finalizes the record, generates a PDF, and emails it to HR.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/50 p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Trainee</span><span className="font-medium">{session.trainee_name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Position</span><span>{session.trainee_position}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span>{session.training_type}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Items completed</span><span className="font-mono">{done}/{total}</span></div>
            </div>
            <div className="space-y-2">
              <Label>Trainer Sign-off (type your full name) <span className="text-destructive">*</span></Label>
              <Input value={signoff} onChange={(e) => setSignoff(e.target.value)} placeholder="Your full name" data-testid="signoff-input" />
              <p className="text-xs text-muted-foreground">By typing your name you attest this training was completed as recorded.</p>
            </div>
            <div className="space-y-2">
              <Label>Email completed record to <span className="text-destructive">*</span></Label>
              <div className="flex flex-wrap gap-2" data-testid="recipients-list">
                {recipients.length === 0 && <span className="text-xs text-muted-foreground">No recipients — add at least one below.</span>}
                {recipients.map((r) => (
                  <Badge key={r} variant="secondary" className="gap-1 pr-1" data-testid={`recipient-${r}`}>
                    {r}
                    <button onClick={() => setRecipients(recipients.filter((x) => x !== r))} className="hover:text-destructive" data-testid={`remove-recipient-${r}`}><X size={12} /></button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={recipInput} onChange={(e) => setRecipInput(e.target.value)} placeholder="name@purplelotuspatientcenter.com"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }} data-testid="recipient-input" />
                <Button type="button" variant="secondary" size="icon" onClick={addRecipient} data-testid="add-recipient-button"><Plus size={16} /></Button>
              </div>
              <p className="text-xs text-muted-foreground">Pre-filled from Settings. Add or remove addresses for this record — type an address and press Enter or +. The email includes the full checklist and a secure download link.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubmitOpen(false)}>Cancel</Button>
            <Button disabled={!signoff.trim() || submitMutation.isPending} onClick={confirmSubmit} className="gap-2" data-testid="confirm-submit-button">
              {submitMutation.isPending && <Loader2 size={16} className="animate-spin" />} Confirm & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MaterialPreview materialId={previewId} open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)} />
    </div>
  );
}
